'use client';

/**
 * Book a talent — open to everyone, signed in or not (Gordon 2026-11-03: "do
 * you have to sign in to book? No, but you leave your details as a customer —
 * add a checkbox to become a BB customer and get offers"). Sent through
 * /api/booking/request. Native date and select inputs: Radix popovers don't
 * work inside this app's dialogs (CLAUDE.md).
 */

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useLocale, useTranslations } from 'next-intl';
import { Loader2, Send, CalendarDays, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { fetchWithAuth } from '@/lib/fetch-with-auth';
import { useToast } from '@/hooks/use-toast';
import type { BookingProfile } from '@/types/booking';

// Common booking currencies — the selector also keeps whatever currency the
// talent themselves priced in, even if it's not on this list.
const CURRENCIES = ['SEK', 'EUR', 'USD', 'GBP', 'NOK', 'DKK', 'CHF', 'CAD', 'AUD', 'JPY'] as const;

/** Best-effort local currency from the browser's locale region (client-only). */
function geoDefaultCurrency(): string {
  try {
    const region =
      (new Intl.Locale(navigator.language).maximize().region || '').toUpperCase();
    const map: Record<string, string> = {
      SE: 'SEK', NO: 'NOK', DK: 'DKK', GB: 'GBP', US: 'USD',
      CA: 'CAD', AU: 'AUD', NZ: 'AUD', JP: 'JPY', CH: 'CHF', IS: 'EUR',
    };
    if (map[region]) return map[region];
    const eurozone = ['DE','FR','ES','IT','NL','BE','AT','PT','FI','IE','GR','SK','SI','EE','LV','LT','LU','CY','MT','HR'];
    if (eurozone.includes(region)) return 'EUR';
  } catch { /* fall through */ }
  return 'EUR';
}

interface BookTalentDialogProps {
  talent: BookingProfile;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  currentUser?: { uid: string; displayName?: string | null; email?: string | null } | null;
}

const EVENT_TYPES = ['performance', 'recording', 'liveEvent', 'workshop', 'privateEvent', 'mixMaster', 'other'] as const;

/** Send a booking enquiry (shared with the profile's Book Me button). */
export async function sendBookingRequest(body: Record<string, unknown>): Promise<'ok' | 'limit' | 'failed'> {
  try {
    const res = await fetchWithAuth('/api/booking/request', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return res.ok ? 'ok' : res.status === 429 ? 'limit' : 'failed';
  } catch {
    return 'failed';
  }
}

export function BookTalentDialog({
  talent,
  open,
  onOpenChange,
  currentUser,
}: BookTalentDialogProps) {
  const t = useTranslations('bookTalent');
  const locale = useLocale();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const [name, setName] = useState(currentUser?.displayName ?? '');
  const [email, setEmail] = useState(currentUser?.email ?? '');
  const [phone, setPhone] = useState('');
  const [eventType, setEventType] = useState('');
  const [proposedDate, setProposedDate] = useState('');
  const [proposedFee, setProposedFee] = useState('');
  // Default: the talent's own pricing currency; otherwise the booker's local one.
  const [currency, setCurrency] = useState(talent.rates?.currency ?? '');
  useEffect(() => {
    if (!currency) setCurrency(geoDefaultCurrency());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [location, setLocation] = useState('');
  const [message, setMessage] = useState('');
  const [optIn, setOptIn] = useState(false); // never pre-ticked
  const [website, setWebsite] = useState(''); // hidden spam trap

  const isPwyw = talent.rates?.type === 'pwyw';
  const minRate =
    isPwyw && talent.rates?.minimumRate
      ? `${talent.rates.minimumRate.toLocaleString()} ${talent.rates.currency}`
      : null;
  const today = new Date().toISOString().slice(0, 10);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast({ variant: 'destructive', title: t('required') });
      return;
    }
    setLoading(true);
    const result = await sendBookingRequest({
      talentId: talent.userId,
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim(),
      eventType: eventType ? t(`event.${eventType}`) : '',
      proposedDate,
      proposedFee: proposedFee ? parseFloat(proposedFee) : undefined,
      currency,
      location: location.trim(),
      message: message.trim(),
      optIn,
      optInText: optIn ? t('optIn') : '',
      website,
      locale,
      source: 'book-talent',
    });
    setLoading(false);
    if (result !== 'ok') {
      toast({ variant: 'destructive', title: t(result === 'limit' ? 'tooMany' : 'failed') });
      return;
    }
    toast({ title: t('sent', { name: talent.displayName }) });
    onOpenChange(false);
    setEventType(''); setProposedDate(''); setProposedFee(''); setLocation(''); setMessage(''); setOptIn(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-card text-card-foreground border border-border max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('title', { name: talent.displayName })}</DialogTitle>
          <DialogDescription>{t('intro', { name: talent.displayName })}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Spam trap: hidden from people, filled by bots. */}
          <input type="text" name="website" value={website} onChange={e => setWebsite(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />

          <div className="space-y-1.5">
            <Label htmlFor="bt-name">{t('name')} <span className="text-destructive">*</span></Label>
            <Input id="bt-name" value={name} onChange={e => setName(e.target.value)} autoComplete="name" required />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="bt-email">{t('email')} <span className="text-destructive">*</span></Label>
              <Input id="bt-email" type="email" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" placeholder="you@example.com" required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bt-phone">{t('phone')}</Label>
              <Input id="bt-phone" type="tel" value={phone} onChange={e => setPhone(e.target.value)} autoComplete="tel" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="bt-event-type">{t('eventType')}</Label>
            <select id="bt-event-type" value={eventType} onChange={e => setEventType(e.target.value)}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
              <option value="">{t('chooseEvent')}</option>
              {EVENT_TYPES.map(k => <option key={k} value={k}>{t(`event.${k}`)}</option>)}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="bt-date" className="flex items-center gap-1.5">
              <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />{t('date')}
            </Label>
            <Input id="bt-date" type="date" min={today} value={proposedDate} onChange={e => setProposedDate(e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="bt-fee" className="flex items-center gap-1.5">
              <DollarSign className="h-3.5 w-3.5" aria-hidden="true" />{t('fee')}
            </Label>
            {isPwyw && minRate && <p className="text-xs text-muted-foreground">{t('minimum', { amount: minRate })}</p>}
            <div className="flex gap-2">
              <Input id="bt-fee" type="number" min={0} step={0.01} value={proposedFee} onChange={e => setProposedFee(e.target.value)} placeholder="0" className="flex-1" />
              <select aria-label={t('currency')} value={currency} onChange={e => setCurrency(e.target.value)}
                className="h-10 w-24 rounded-md border border-input bg-background px-2 text-sm">
                {(currency && !CURRENCIES.includes(currency as typeof CURRENCIES[number]) ? [currency, ...CURRENCIES] : [...CURRENCIES])
                  .map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="bt-location">{t('location')}</Label>
            <Input id="bt-location" value={location} onChange={e => setLocation(e.target.value)} autoComplete="address-level2" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="bt-message">{t('message')} <span className="text-destructive">*</span></Label>
            <Textarea id="bt-message" value={message} onChange={e => setMessage(e.target.value)} placeholder={t('messageHint')} rows={4} required />
          </div>

          {/* Become a BB customer — an active choice, never pre-ticked. */}
          <label className="flex items-start gap-2.5 rounded-md border border-border p-3 text-sm">
            <input type="checkbox" checked={optIn} onChange={e => setOptIn(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-primary" />
            <span>{t('optIn')}</span>
          </label>
          <p className="text-xs text-muted-foreground">
            {t('privacyNote', { name: talent.displayName })}{' '}
            <Link href="/privacy" className="underline underline-offset-2 hover:text-foreground">{t('privacyLink')}</Link>
          </p>

          <Button type="submit" disabled={loading} className="w-full">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Send className="h-4 w-4" aria-hidden="true" />}
            {loading ? t('sending') : t('send')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
